import { Button, Logo, Typography } from "@/components/atoms";
import { PhoneBadgeIcon } from "@/components/atoms/icons";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useContentfulRevision, useTranslation, useUIColors } from "@/core/hooks";
import {
  LANDING_PAGE_ACTION,
  LANDING_PAGE_SCREEN,
} from "@/core/constants/dynatraceCatalog";
import {
  landingSpaceDetailProperties,
  trackCatalogEvent,
} from "@/core/services/trackDynatraceCatalog";
import { logger } from "@/core/logger";
import { openSupportPhoneDialer } from "@/core/utils/phoneUtils";
import {
  BloomSpaceCard,
  fetchBloomUniversalHub,
  type BloomUniversalHubContent,
  type BloomUniversalSpaceContent,
} from "@/modules/bloom-universal";
import { BLOOM_UNIVERSAL_HUB_FALLBACK } from "@/modules/bloom-universal/constants/bloomUniversalContent.fallback";
import { mapBloomUniversalHubFields } from "@/modules/bloom-universal/services/mapBloomUniversalContent";
import { resolveBloomUniversalImageUrl } from "@/modules/bloom-universal/utils/resolveImageUrl";
import { router } from "expo-router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Image, Platform, Pressable, ScrollView, StyleSheet, useWindowDimensions, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { getExploreCardSizes, getHeroHeight } from "../constants/exploreCardLayout";
import { useSingleTapAction } from "@/core/hooks/useSingleTapAction";
import { DirectionalArrowIcon } from "./DirectionalArrowIcon";
import { HeroOverlayGradient } from "./HeroOverlayGradient";

/** Gap between bento tiles — matches the grid styles below. */
const GRID_GAP = 8;

/** Figma height for the half-small pair. Width is flex-based so iOS can shrink. */
const HALF_SMALL_HEIGHT = 110;

/** How far the white content card overlaps the hero image's bottom edge to round its top corners over the photo. */
const CARD_OVERLAP = 24;

/** Mobile's own bento mosaic key — differs from Contentful's `facilities` for this one entry. */
function toDetailsScreenKey(spaceKey: string): string {
  return spaceKey === "facilities" ? "facilitiesManagement" : spaceKey;
}

/**
 * Bloom brand landing screen — shown once on app launch, before Welcome
 * (register/login). Not specific to the ADHA journey or any single product
 * line; represents the whole Bloom group (Facilities Management, Hospitality,
 * Properties, Retail, Education, Landscape).
 *
 * Fully Contentful-driven (same `bloomUniversalHub` entry web's
 * BloomUniversalHubScreen renders) — no local static copy or images, same
 * as web (see bloomUniversalContent.fallback.ts: "Images and icons come
 * from Contentful; this JSON has no local media URLs"). Blank while the
 * fetch is in flight; on fetch failure, falls back to the same text-only
 * fallback JSON web's useBloomUniversalHubContent uses — its image fields
 * are empty, so cards/hero just render without a photo, matching web.
 */
export function BloomExperienceScreen() {
  const colors = useUIColors();
  const { t, currentLanguage, toggleLanguage } = useTranslation();
  const insets = useSafeAreaInsets();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const cardSizes = useMemo(() => getExploreCardSizes(screenWidth), [screenWidth]);
  // Shorter than the shared default — this hero's copy is top-anchored
  // (eyebrow/headline/CTA near the top, not bottom-anchored like Details),
  // so a shorter hero keeps the "Explore Bloom World" card close under the
  // button instead of leaving a big empty gap of hero image beneath it.
  const heroHeight = useMemo(() => getHeroHeight(screenHeight, 0.34), [screenHeight]);
  const fallbackHub = useMemo(
    () => mapBloomUniversalHubFields(BLOOM_UNIVERSAL_HUB_FALLBACK),
    [],
  );

  const [hub, setHub] = useState<BloomUniversalHubContent | null>(null);
  // Reload the hub after an app-wide Contentful refresh (app foreground).
  const contentfulRevision = useContentfulRevision();
  useEffect(() => {
    const locale = currentLanguage === "ar" ? "ar" : "en";
    let cancelled = false;
    void fetchBloomUniversalHub(locale)
      .then((result) => {
        logger.debug("BloomExperienceScreen", "Contentful hub response", { locale, hub: result });
        if (!cancelled) {
          setHub(result);
        }
      })
      .catch((error) => {
        logger.warn("BloomExperienceScreen", "Contentful hub fetch failed, using fallback content", { locale, error });
        if (!cancelled) {
          setHub(fallbackHub);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentLanguage, fallbackHub, contentfulRevision]);

  // Card background is the space's `tileImageUrl`, same as web's hub mosaic.
  const mosaicSpaces = useMemo<readonly BloomUniversalSpaceContent[]>(
    () => hub?.spaces ?? [],
    [hub],
  );

  const heroBackgroundUrl = resolveBloomUniversalImageUrl(hub?.heroBackgroundUrl);

  useCatalogDynatraceView(
    LANDING_PAGE_SCREEN.HUB,
    LANDING_PAGE_ACTION.VIEWED,
    Boolean(hub),
  );

  // Same toggle as LoginScreen's LogoHeader — the hub refetches in the new
  // locale via the currentLanguage effect above.
  const handleLanguageToggle = useCallback(async () => {
    try {
      await toggleLanguage();
    } catch (error) {
      logger.error("BloomExperienceScreen", "Failed to toggle language", error);
    }
  }, [toggleLanguage]);

  // Rapid repeat taps would otherwise push the same screen several times.
  const runSingleTap = useSingleTapAction();

  const handleGetStarted = useCallback(() => {
    runSingleTap(() => {
      trackCatalogEvent({
        screen: LANDING_PAGE_SCREEN.HUB,
        actionName: LANDING_PAGE_ACTION.HUB_CTA_CLICK,
        eventType: "action",
        properties: { locale: currentLanguage },
      });
      router.push("/login");
    });
  }, [runSingleTap, currentLanguage]);

  const handleOpenSpace = useCallback((spaceKey: string) => {
    runSingleTap(() => {
      const space = mosaicSpaces.find((s) => s.spaceKey === spaceKey);
      trackCatalogEvent({
        screen: LANDING_PAGE_SCREEN.HUB,
        actionName: LANDING_PAGE_ACTION.EXPLORE_CARD_CLICK,
        eventType: "action",
        properties: landingSpaceDetailProperties({
          spaceKey,
          spaceName: space?.title,
          experienceName: space?.title,
          locale: currentLanguage,
        }),
      });
      // "Bloom Properties" has a real in-app flow already — skip the generic
      // category details screen and open it directly. Every other category
      // still opens its own details screen (see BloomExperienceDetailsScreen's
      // "Discover more" for those).
      if (spaceKey === "properties") {
        router.push("/guest-home");
        return;
      }
      router.push({
        pathname: "/bloom-experience-details",
        params: { key: toDetailsScreenKey(spaceKey) },
      });
    });
  }, [runSingleTap, mosaicSpaces, currentLanguage]);

  // Same bento shape as before (tall / wide / 2 half-small / 2 half) — only
  // WHICH space lands in which fixed slot changed: it's now positional by
  // `sortOrder` rank (mosaicSpaces is already sorted), not by category
  // identity. A slot renders nothing if there's no space at that rank
  // (fewer than 6 active spaces).
  const renderSpaceSlot = (
    index: number,
    sizeProps: { readonly width?: number; readonly height: number },
  ) => {
    const space = mosaicSpaces[index];
    if (!space) {
      return null;
    }
    return (
      <View
        key={space.spaceKey}
        style={[
          { height: sizeProps.height },
          sizeProps.width != null ? { width: sizeProps.width } : { width: "100%" },
        ]}
      >
        {/* BloomSpaceCard still owns the background image and the tap
            target — not its title/arrow/type-icon, all rendered as a local
            overlay instead: (1) title/arrow because BloomSpaceCard's footer
            is always bottom-anchored regardless of `titlePlacement`
            (`titleTopPercent` prop is dead code there — mobile-only
            workaround pending a shared-component fix); (2) the type icon
            because BloomSpaceCard insets it by the desktop default (25px)
            on native — none of its `Platform.OS === 'web'` phone/medium
            checks apply here — which reads as "not in the corner" on these
            small tiles. */}
        <BloomSpaceCard
          variant="action"
          spaceKey={space.spaceKey}
          title=""
          showArrow={false}
          showTypeIcon={false}
          height={sizeProps.height}
          {...(sizeProps.width != null ? { width: sizeProps.width } : {})}
          gutter={0}
          // BloomSpaceCard defaults to 18 (desktop) and only drops to 8 on
          // Platform.OS === 'web' at phone width — never true here.
          borderRadius={8}
          {...(space.tileImageUrl ? { tileImageUrl: space.tileImageUrl } : {})}
          onPress={() => handleOpenSpace(space.spaceKey)}
        />
        <View style={styles.slotOverlay} pointerEvents="none">
          <Typography
            variant="subhead"
            color="white"
            numberOfLines={2}
            ellipsizeMode="tail"
            style={styles.slotTitle}
          >
            {space.title}
          </Typography>
        </View>
        <View style={styles.slotArrowBadge} pointerEvents="none">
          <DirectionalArrowIcon size={13} color={colors.white} />
        </View>
      </View>
    );
  };

  // Native only — web's equivalent (BloomUniversalHubScreen) already
  // self-guards the same way (`if (Platform.OS !== 'web') return null`);
  // this is the mirror image so this screen never renders on web even via
  // a direct route hit, not just via app/index.tsx's platform branch.
  if (Platform.OS === 'web') {
    return null;
  }

  if (!hub) {
    return (
      <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]} />
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
      {/* Fixed header — a sibling of the ScrollView, not scroll content, so
          it stays pinned above the hero image and the white content card
          scrolling beneath it. Solid white, matching the Details screen's
          header now that both use the same treatment. No back arrow here —
          this is the app's landing screen, nothing to go back to, so the
          language toggle takes that slot. The logo is absolutely centered
          since the toggle text and the phone badge differ in width. */}
      <View style={[styles.header, { paddingTop: insets.top + 8, backgroundColor: colors.white }]}>
        <View style={[styles.headerLogo, { top: insets.top + 8 }]} pointerEvents="none">
          <Logo size="medium" />
        </View>
        {/* Sits in the back-arrow slot (Details screen's leading icon) —
            shows the opposite language, same as LoginScreen's LogoHeader. */}
        <Pressable onPress={handleLanguageToggle} hitSlop={8} accessibilityRole="button">
          <Typography
            variant="body"
            numberOfLines={1}
            style={[styles.languageText, { color: colors.fontPrimary }]}
          >
            {currentLanguage === "en" ? "العربية" : "English"}
          </Typography>
        </Pressable>
        <Pressable onPress={openSupportPhoneDialer} hitSlop={8} accessibilityRole="button">
          <PhoneBadgeIcon size={44} />
        </Pressable>
      </View>

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={[styles.heroWrapper, { height: heroHeight }]}>
          {heroBackgroundUrl ? (
            <Image
              source={{ uri: heroBackgroundUrl }}
              style={StyleSheet.absoluteFillObject}
              resizeMode="cover"
            />
          ) : (
            <View style={[StyleSheet.absoluteFillObject, { backgroundColor: colors.buttonDefault }]} />
          )}
          <HeroOverlayGradient />

          <View style={styles.heroCopy}>
            <Typography
              variant="caption"
              style={[styles.heroLabel, { color: colors.white }]}
            >
              {hub.experienceEyebrow}
            </Typography>
            <Typography
              variant="title1"
              color="white"
              style={styles.heroTitle}
            >
              {hub.headline}
            </Typography>
            <Typography
              variant="subhead"
              color="white"
              style={styles.heroSubtitle}
            >
              {hub.subheadline}
            </Typography>

            <Button
              title={hub.ctaLabel}
              variant="primary"
              // "dark" here, not "light" — Button computes its primary
              // text/icon color as the OPPOSITE theme's buttonDefault
              // (see Button.tsx's primaryTextColor + its cloneElement on
              // `trailing`, which always overrides whatever color a passed
              // icon has). theme="light" resolved to near-white (assuming
              // a dark button bg), invisible against the white background
              // this button actually has (via the style override below).
              // theme="dark" makes both resolve to #1a1a1a instead.
              theme="dark"
              fullWidth={false}
              trailing={<DirectionalArrowIcon size={15} />}
              onPress={handleGetStarted}
              style={{ backgroundColor: colors.white, height: 34 }}
              // Figma: Font/Size/XS (14) Medium, LH 100%, letter-spacing 0.
              textStyle={styles.ctaLabel}
            />
          </View>
        </View>

        <View style={[styles.contentCard, { minHeight: screenHeight - heroHeight + CARD_OVERLAP, backgroundColor: colors.white }]}>
          <View style={styles.exploreSection}>
            <Typography
              variant="headline"
              style={[styles.exploreTitle, { color: colors.fontHighContrast }]}
            >
              {hub.exploreTitle || t("bloomExperience.exploreTitle")}
            </Typography>
            <Typography
              variant="body"
              style={[styles.exploreSubtitle, { color: colors.fontSecondary }]}
            >
              {hub.exploreSubtitle || t("bloomExperience.exploreSubtitle")}
            </Typography>

            <View style={[styles.gridRow, { height: cardSizes.cardTall.height }]}>
              {renderSpaceSlot(0, cardSizes.cardTall)}
              <View style={[styles.gridColumn, { height: cardSizes.cardTall.height }]}>
                {renderSpaceSlot(1, { height: cardSizes.cardWide.height })}
                <View style={styles.gridRowFill}>
                  <View style={styles.cardHalf}>
                    {renderSpaceSlot(2, { height: HALF_SMALL_HEIGHT })}
                  </View>
                  <View style={styles.cardHalf}>
                    {renderSpaceSlot(3, { height: HALF_SMALL_HEIGHT })}
                  </View>
                </View>
              </View>
            </View>

            <View style={styles.gridRowSmall}>
              <View style={styles.cardHalf}>
                {renderSpaceSlot(4, { height: cardSizes.cardHalf.height })}
              </View>
              <View style={styles.cardHalf}>
                {renderSpaceSlot(5, { height: cardSizes.cardHalf.height })}
              </View>
            </View>
          </View>

          <View style={{ height: insets.bottom + 24 }} />
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: "relative",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  // Same height as the phone badge so the logo lines up with the actions row.
  headerLogo: {
    position: "absolute",
    left: 0,
    right: 0,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
  },
  // Matches LogoHeader's languageText.
  languageText: {
    fontWeight: "500",
    lineHeight: 20,
    includeFontPadding: false,
    textAlignVertical: "center",
  },
  scrollContent: {
    flexGrow: 1,
  },
  scrollView: {
    flex: 1,
  },
  heroWrapper: {
    width: "100%",
    justifyContent: "flex-start",
  },
  // Pinned 40px below the hero's top edge (not bottom-anchored) — eyebrow,
  // headline, subtitle, then the CTA button below them.
  heroCopy: {
    alignItems: "flex-start",
    width: "100%",
    paddingHorizontal: 24,
    paddingTop: 40,
  },
  // textAlign "auto" (not "left") on this screen's copy: follows the text's own
  // direction, so Arabic right-aligns without an isRTL branch that could
  // double-flip against native's own RTL handling.
  heroLabel: {
    textAlign: "auto",
    fontFamily: "Matter-SemiBold",
    letterSpacing: 1,
    textTransform: "uppercase",
    marginBottom: 12,
  },
  heroTitle: {
    textAlign: "auto",
    marginBottom: 8,
  },
  heroSubtitle: {
    textAlign: "auto",
    fontFamily: "Matter-Medium",
    fontWeight: "500",
    fontSize: 20,
    lineHeight: 20,
    letterSpacing: 0,
    marginBottom: 24,
  },
  // Figma: Font/Size/XS (14) Medium, LH 100% (= fontSize), letter-spacing 0.
  ctaLabel: {
    fontFamily: "Matter-Medium",
    fontWeight: "500",
    fontSize: 14,
    lineHeight: 14,
    letterSpacing: 0,
    textAlign: "center",
    textAlignVertical: "center",
  },
  // Overlaps the hero's bottom edge by CARD_OVERLAP so its rounded top
  // corners cut into the photo, matching the Figma bottom-sheet-over-image
  // treatment instead of a hard seam between hero and content.
  contentCard: {
    marginTop: -CARD_OVERLAP,
    borderTopLeftRadius: CARD_OVERLAP,
    borderTopRightRadius: CARD_OVERLAP,
    paddingTop: CARD_OVERLAP,
  },
  exploreSection: {
    padding: 16,
    gap: 12,
  },
  exploreTitle: {
    textAlign: "auto",
    fontFamily: "Matter-SemiBold",
  },
  exploreSubtitle: {
    textAlign: "auto",
    marginBottom: 24,
    fontFamily: "Matter-Regular",
    fontWeight: "400",
    fontSize: 14,
    lineHeight: 20,
    letterSpacing: 0,
  },
  gridRow: {
    flexDirection: "row",
    gap: GRID_GAP,
  },
  gridColumn: {
    flex: 1,
    gap: GRID_GAP,
    // iOS Yoga won't shrink flex children below their content min-width
    // (the half-small tiles used a fixed 105px and overflowed the column).
    minWidth: 0,
  },
  gridRowSmall: {
    flexDirection: "row",
    gap: GRID_GAP,
  },
  // Fills whatever vertical space is left in gridColumn after the fixed-height
  // wide card. The half-small pair inside has its own fixed (Figma) height
  // now, shorter than this row — flex-end bottom-anchors them so their
  // bottom edge lines up with the tall card's bottom edge, instead of
  // floating at the top of the leftover space.
  gridRowFill: {
    flex: 1,
    flexDirection: "row",
    gap: GRID_GAP,
    alignItems: "flex-end",
    minWidth: 0,
  },
  cardHalf: {
    flex: 1,
    minWidth: 0,
  },
  slotOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "flex-end",
    // Logical start edge: bottom-left in English, bottom-right in Arabic
    // (opposite the arrow badge at `end`), independent of textAlign.
    alignItems: "flex-start",
    paddingHorizontal: 12,
    // Clears slotArrowBadge (24px + 8px bottom inset) so the title never
    // sits underneath the arrow badge in the bottom-right corner.
    paddingBottom: 12,
  },
  slotTitle: {
    textAlign: "auto",
    fontFamily: "Matter-Medium",
    fontWeight: "500",
    // Badge is 24px at end: 8. Extra end padding keeps long titles
    // ellipsizing before the arrow instead of drawing over it. Logical
    // (end, not right) so it tracks the badge to the left side in Arabic.
    paddingEnd: 28,
  },
  slotArrowBadge: {
    position: "absolute",
    end: 8,
    bottom: 8,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "rgba(26,26,26,0.5)",
    alignItems: "center",
    justifyContent: "center",
  },
});

export default BloomExperienceScreen;
